// Libs
import classNames from 'classnames/bind';
import { Grid2, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseAutocomplete, BaseInput } from '~/components/common';
// Others
import { TypeCrmEnum } from '~/utils/enum/common';
import { IFormClient } from '~/utils/interface/client';
import { IBaseOption } from '~/utils/interface/common';
import { formatPhoneNumber } from '~/utils/helper/common';
import { EMPTY_STRING, optionContactTypeClient } from '~/utils/constants/common';
// Styles, images, icons
import styles from './AdditionalContactClientForm.module.scss';
import { icons } from '~/assets';

type Props = {
  onRemoveContact?: (id: string) => void;
};

const cx = classNames.bind(styles);

const AdditionalContactClientForm = (props: Props) => {
  //#region Destructuring Props
  const { onRemoveContact } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<IFormClient>();

  const {
    fields: additionalContactsFields,
    append: appendAdditionalContacts,
    remove: removeAdditionalContacts,
  } = useFieldArray({
    control,
    name: 'additionalContact',
  });

  const { additionalContact } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddAdditionalContact = () => {
    appendAdditionalContacts({
      countryCode: EMPTY_STRING,
      email: EMPTY_STRING,
      firstName: EMPTY_STRING,
      lastName: EMPTY_STRING,
      phoneNumber: EMPTY_STRING,
      contactType: EMPTY_STRING,
    });
  };

  const handleGetOptionContactTypeClient = (index: number) => {
    const selectedRelationships = additionalContact?.map((c) => c.contactType) || [];

    const selected = selectedRelationships.filter((_, i) => i !== index);

    return optionContactTypeClient.filter((opt) => !selected.includes(opt.value));
  };
  //#endregion Handle Function

  return (
    <Stack id='additionalContactClientFormComponent' gap={'14px'}>
      <Typography className={cx('title')}>{t('client_form_title_additional_contact')}</Typography>

      {additionalContactsFields.map((contact, index) => (
        <>
          <Grid2
            key={contact?.id || index}
            container
            alignItems={'end'}
            justifyContent={'space-between'}
            rowSpacing={3}
            columnSpacing={5}
          >
            <Grid2 size={2.5}>
              <Controller
                control={control}
                key={contact?.id ? `${contact?.id}-firstName` : `${index}-firstName`}
                name={`additionalContact.${index}.firstName`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('client_form_title_first_name_contact')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.additionalContact?.[index]?.firstName?.message}
                    />
                  );
                }}
              />
            </Grid2>

            {/*<Grid2 size={2.4}>*/}
            {/*  <Controller*/}
            {/*    control={control}*/}
            {/*    name={`additionalContact.${index}.lastName`}*/}
            {/*    render={({ field: { onChange, value } }) => {*/}
            {/*      return (*/}
            {/*        <BaseInput*/}
            {/*          height={36}*/}
            {/*          label={t('client_form_title_last_name_contact')}*/}
            {/*          value={value}*/}
            {/*          onChange={onChange}*/}
            {/*          errorMessage={errors?.additionalContact?.[index]?.lastName?.message}*/}
            {/*        />*/}
            {/*      );*/}
            {/*    }}*/}
            {/*  />*/}
            {/*</Grid2>*/}

            <Grid2 size={2.5}>
              <Controller
                control={control}
                key={contact?.id ? `${contact?.id}-email` : `${index}-email`}
                name={`additionalContact.${index}.email`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('client_form_title_email_contact')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.additionalContact?.[index]?.email?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={2.5}>
              <Controller
                control={control}
                name={`additionalContact.${index}.phoneNumber`}
                key={contact?.id ? `${contact?.id}-phoneNumber` : `${index}-phoneNumber`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      height={36}
                      label={t('client_form_title_phone_contact')}
                      value={value}
                      onChange={(event) => {
                        onChange(formatPhoneNumber(event.target.value));
                      }}
                      errorMessage={errors?.additionalContact?.[index]?.phoneNumber?.message}
                    />
                  );
                }}
              />
            </Grid2>

            <Grid2 size={2.5}>
              <Controller
                control={control}
                name={`additionalContact.${index}.contactType`}
                key={contact?.id ? `${contact?.id}-contactType` : `${index}-contactType`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseAutocomplete
                      height={40}
                      showIcon={false}
                      placeholder={EMPTY_STRING}
                      value={optionContactTypeClient.find((item) => item.value === value) || null}
                      options={handleGetOptionContactTypeClient(index)}
                      textSearch={optionContactTypeClient.find((item) => item.value === value)?.label || value}
                      onTextSearchChange={onChange}
                      onChange={(option) => {
                        const data = option as IBaseOption;

                        onChange(data?.value);
                      }}
                      label={t('client_form_title_contact_type')}
                      errorMessage={errors?.additionalContact?.[index]?.contactType?.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={1}>
              <IconButton
                key={contact?.id ? `${contact?.id}-delete` : `${index}-delete`}
                className={cx('buttonIcon')}
                onClick={() => {
                  removeAdditionalContacts(index);
                  onRemoveContact?.(contact.idDetail || EMPTY_STRING);
                }}
              >
                <img
                  className={cx('iconDelete')}
                  src={icons.commonIconTrash}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                />
              </IconButton>
            </Grid2>
          </Grid2>
        </>
      ))}
      <Typography component={'span'} className={cx('buttonAdd')} onClick={handleAddAdditionalContact}>
        {t('common_button_add_item', { ns: 'common/base' })}
      </Typography>
    </Stack>
  );
};

export default AdditionalContactClientForm;
